import { EnterpriseIntegration, EnterpriseIntegrationResult, useRepositoryQuery } from '@mews-commander/data-access';
import { VALID_SWEDISH_INTEGRATION_TYPES } from './constants';

interface IntegrationEnabled {
    hasSwedishFiscalIntegration: boolean;
    isLoading: boolean;
    data: EnterpriseIntegration[];
}
/**
 * Custom hook that checks if Swedish fiscal integration is enabled and returns relevant integration data.
 *
 * This hook queries all integrations and filters them to determine if Swedish fiscal reporting
 * integrations are available and enabled. It processes the data to return only valid Swedish
 * integration types when the feature is enabled.
 *
 * @returns An object containing:
 *   - `hasSwedishFiscalIntegration`: Boolean indicating if Swedish fiscal integration is enabled
 *   - `isLoading`: Boolean indicating if the query is currently loading
 *   - `data`: Array of EnterpriseIntegration objects that are valid Swedish integration types,
 *     or empty array if integration is disabled or no valid integrations exist
 */
export const useHasSwedishIntegrationEnabled = (): IntegrationEnabled => {
    const { data, isLoading } = useRepositoryQuery(
        'integrations',
        'getAll',
        {},
        {
            select: (currentData) => {
                const isEnabled = hasSwedishFiscalIntegrationEnabled(currentData);
                if (!isEnabled) {
                    return {
                        hasSwedishFiscalIntegration: isEnabled,
                        data: [] as EnterpriseIntegration[],
                    };
                }
                return {
                    hasSwedishFiscalIntegration: isEnabled,
                    data:
                        currentData.integrations?.filter(
                            (integration) =>
                                !!integration.type && VALID_SWEDISH_INTEGRATION_TYPES.includes(integration.type)
                        ) || [],
                };
            },
        }
    );

    return { hasSwedishFiscalIntegration: !!data?.hasSwedishFiscalIntegration, isLoading, data: data?.data || [] };
};

/**
 * Checks if Swedish fiscal integration is enabled for the given enterprise.
 * @param data - The enterprise integration result containing integration configurations
 * @returns True if at least one Swedish fiscal integration is enabled, false otherwise
 */
export const hasSwedishFiscalIntegrationEnabled = (data: EnterpriseIntegrationResult): boolean => {
    if (!data || !data.integrations) {
        return false;
    }
    return data.integrations.some(
        (integration) =>
            !!integration.type && VALID_SWEDISH_INTEGRATION_TYPES.includes(integration.type) && integration.isEnabled
    );
};
